import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { FeatureGate } from "@/components/FeatureGate";
import {
  Inbox,
  MessageCircle,
  Phone,
  Instagram,
  Facebook,
  RefreshCw,
  Search,
  Loader2,
  ChevronDown,
  ChevronRight,
  Filter,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useI18n } from "@/lib/i18n";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_app/inbox")({
  component: () => (
    <FeatureGate feature="ai_studio">
      <InboxPage />
    </FeatureGate>
  ),
  head: () => ({
    meta: [
      { title: "Inbox · AdKavi" },
      { name: "description", content: "AI-handled conversations across WhatsApp, Instagram, and Messenger." },
    ],
  }),
});

type Channel = "whatsapp" | "instagram" | "messenger";
type Status = "replied" | "pending" | "failed";

type Conversation = {
  id: string;
  channel: Channel;
  contact: string;
  incoming_message: string;
  ai_reply: string;
  status: Status;
  created_at: string;
};

const CHANNEL_LABELS: Record<Channel, string> = {
  whatsapp: "WhatsApp",
  instagram: "Instagram",
  messenger: "Messenger",
};

const CHANNEL_ICONS: Record<Channel, React.ReactNode> = {
  whatsapp: <Phone className="w-3.5 h-3.5" />,
  instagram: <Instagram className="w-3.5 h-3.5" />,
  messenger: <Facebook className="w-3.5 h-3.5" />,
};

const CHANNEL_COLORS: Record<Channel, string> = {
  whatsapp: "text-emerald-500",
  instagram: "text-pink-500",
  messenger: "text-blue-500",
};

function statusBadgeClass(status: Status) {
  if (status === "replied") return "border-emerald-500/40 text-emerald-500";
  if (status === "failed") return "border-destructive/40 text-destructive";
  return "border-amber-500/40 text-amber-500";
}

function InboxPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [channelFilter, setChannelFilter] = useState<"all" | Channel>("all");
  const [statusFilter, setStatusFilter] = useState<"all" | Status>("all");
  const [expanded, setExpanded] = useState<string | null>(null);

  const loadConversations = useCallback(async (showSpinner = false) => {
    if (!user) return;
    if (showSpinner) setLoading(true);
    try {
      const { data } = await supabase
        .from("conversations")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(200);
      if (data) setConversations(data as Conversation[]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user) return;
    loadConversations();

    let ch: ReturnType<typeof supabase.channel> | null = null;
    try {
      ch = supabase.channel(`inbox-${user.id}-${Math.random().toString(36).slice(2, 8)}`);
      ch.on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "conversations", filter: `user_id=eq.${user.id}` },
        (payload) => setConversations((prev) => [payload.new as Conversation, ...prev]),
      );
      ch.subscribe();
    } catch (e) {
      console.warn("inbox realtime subscribe failed (non-critical)", e);
    }
    return () => {
      if (ch) {
        try { supabase.removeChannel(ch); } catch { /* noop */ }
      }
    };
  }, [user?.id, loadConversations]);

  const filtered = conversations.filter((c) => {
    if (channelFilter !== "all" && c.channel !== channelFilter) return false;
    if (statusFilter !== "all" && c.status !== statusFilter) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      if (
        !c.contact.toLowerCase().includes(q) &&
        !c.incoming_message.toLowerCase().includes(q) &&
        !c.ai_reply.toLowerCase().includes(q)
      ) return false;
    }
    return true;
  });

  const counts = {
    total: conversations.length,
    replied: conversations.filter((c) => c.status === "replied").length,
    pending: conversations.filter((c) => c.status === "pending").length,
    failed: conversations.filter((c) => c.status === "failed").length,
  };

  const STATUS_LABELS: Record<Status, string> = {
    replied: t("inbox.stat.replied"),
    pending: t("inbox.stat.pending"),
    failed: t("inbox.stat.failed"),
  };

  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2.5">
            <Inbox className="w-7 h-7 text-orange-400" />
            <span className="bg-gradient-to-r from-orange-400 to-primary bg-clip-text text-transparent">{t("inbox.title")}</span>
          </h1>
          <p className="mt-1 text-muted-foreground text-sm">
            {t("inbox.subtitle")}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => loadConversations(true)}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          {t("inbox.refresh")}
        </Button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard label={t("inbox.stat.total")} value={counts.total} />
        <StatCard label={t("inbox.stat.replied")} value={counts.replied} color="text-emerald-500" />
        <StatCard label={t("inbox.stat.pending")} value={counts.pending} color="text-amber-500" />
        <StatCard label={t("inbox.stat.failed")} value={counts.failed} color="text-destructive" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input
            type="search"
            placeholder={t("inbox.search.placeholder")}
            className="pl-8 h-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={channelFilter} onValueChange={(v) => setChannelFilter(v as typeof channelFilter)}>
          <SelectTrigger className="w-[150px] h-9">
            <Filter className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
            <SelectValue placeholder={t("inbox.filter.channel")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("inbox.filter.allChannels")}</SelectItem>
            <SelectItem value="whatsapp">WhatsApp</SelectItem>
            <SelectItem value="instagram">Instagram</SelectItem>
            <SelectItem value="messenger">Messenger</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
          <SelectTrigger className="w-[140px] h-9">
            <SelectValue placeholder={t("inbox.filter.status")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("inbox.filter.allStatuses")}</SelectItem>
            <SelectItem value="replied">{t("inbox.stat.replied")}</SelectItem>
            <SelectItem value="pending">{t("inbox.stat.pending")}</SelectItem>
            <SelectItem value="failed">{t("inbox.stat.failed")}</SelectItem>
          </SelectContent>
        </Select>
        {(search || channelFilter !== "all" || statusFilter !== "all") && (
          <Button
            variant="ghost"
            size="sm"
            className="h-9"
            onClick={() => { setSearch(""); setChannelFilter("all"); setStatusFilter("all"); }}
          >
            {t("inbox.filter.clear")}
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border/60 overflow-hidden">
        <div className="hidden sm:grid grid-cols-[120px_140px_1fr_100px] gap-3 px-4 py-2.5 text-xs font-medium text-muted-foreground bg-muted/30 border-b border-border/60">
          <div>{t("inbox.col.channel")}</div>
          <div>{t("inbox.col.contact")}</div>
          <div>{t("inbox.col.message")}</div>
          <div className="text-right">{t("inbox.col.status")}</div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground/50" />
            {t("inbox.loading")}
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground flex flex-col items-center gap-3">
            <MessageCircle className="w-10 h-10 text-muted-foreground/30" />
            {conversations.length === 0 ? (
              <>
                <p className="font-medium text-foreground/60">{t("inbox.empty.title")}</p>
                <p className="max-w-xs text-xs">
                  {t("inbox.empty.desc")}
                </p>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/settings">{t("inbox.empty.setup")}</Link>
                </Button>
              </>
            ) : (
              <p>{t("inbox.empty.noMatch")}</p>
            )}
          </div>
        ) : (
          <div className="divide-y divide-border/40">
            {filtered.map((c) => (
              <ConversationRow
                key={c.id}
                convo={c}
                isExpanded={expanded === c.id}
                onToggle={() => setExpanded(expanded === c.id ? null : c.id)}
                statusLabels={STATUS_LABELS}
              />
            ))}
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="px-4 py-2.5 text-xs text-muted-foreground border-t border-border/40 bg-muted/20">
            {t("inbox.showing", { shown: String(filtered.length), total: String(conversations.length) })}
          </div>
        )}
      </div>
    </section>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card/40 px-4 py-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-2xl font-bold mt-0.5 tabular-nums ${color ?? ""}`}>{value}</p>
    </div>
  );
}

function ConversationRow({
  convo,
  isExpanded,
  onToggle,
  statusLabels,
}: {
  convo: Conversation;
  isExpanded: boolean;
  onToggle: () => void;
  statusLabels: Record<Status, string>;
}) {
  const { t } = useI18n();
  const channelColor = CHANNEL_COLORS[convo.channel] ?? "text-muted-foreground";

  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left hover:bg-muted/20 transition-colors"
      >
        {/* Mobile layout */}
        <div className="sm:hidden flex items-start gap-3 px-4 py-3">
          <span className={`mt-0.5 ${channelColor}`}>
            {CHANNEL_ICONS[convo.channel]}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-0.5">
              <span className="text-xs font-medium text-muted-foreground truncate">{convo.contact}</span>
              <Badge variant="outline" className={`text-[10px] shrink-0 ${statusBadgeClass(convo.status)}`}>
                {statusLabels[convo.status]}
              </Badge>
            </div>
            <p className="text-sm truncate text-foreground/80">{convo.incoming_message}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {CHANNEL_LABELS[convo.channel]} · {new Date(convo.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
            </p>
          </div>
          {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />}
        </div>

        {/* Desktop layout */}
        <div className="hidden sm:grid grid-cols-[120px_140px_1fr_100px] gap-3 items-center px-4 py-3 text-sm">
          <div className={`flex items-center gap-1.5 font-medium ${channelColor}`}>
            {CHANNEL_ICONS[convo.channel]}
            <span className="text-xs">{CHANNEL_LABELS[convo.channel]}</span>
          </div>
          <div className="text-muted-foreground text-xs truncate">{convo.contact}</div>
          <div className="truncate text-foreground/80 flex items-center gap-1.5">
            {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
            <span className="truncate">{convo.incoming_message}</span>
          </div>
          <div className="flex justify-end">
            <Badge variant="outline" className={`text-[10px] ${statusBadgeClass(convo.status)}`}>
              {statusLabels[convo.status]}
            </Badge>
          </div>
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 pt-1 space-y-3 bg-muted/10 border-t border-border/30">
          <div className="rounded-lg bg-background/60 border border-border/40 p-3 space-y-1">
            <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{t("inbox.expanded.lead")}</p>
            <p className="text-sm leading-relaxed">{convo.incoming_message}</p>
          </div>
          {convo.ai_reply && (
            <div className="rounded-lg bg-primary/5 border border-primary/20 p-3 space-y-1">
              <p className="text-[11px] font-medium text-primary uppercase tracking-wider">{t("inbox.expanded.aiReply")}</p>
              <p className="text-sm leading-relaxed">{convo.ai_reply}</p>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            {new Date(convo.created_at).toLocaleString(undefined, {
              weekday: "short",
              month: "short",
              day: "numeric",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
        </div>
      )}
    </>
  );
}
